// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { ConsentInfo } from '@api/types';

/**
 * Biometric re-consent state (ME-11391).
 *
 * The backend returns when the biometric consent lapses as an RFC 3339
 * date-time on `consentInfos[].expiresAt`. The renewal window opens
 * `RECONSENT_WINDOW_DAYS` before that instant and stays open once it is past:
 * an expired consent needs renewing at least as much as one about to expire.
 *
 * Absent or unparseable dates mean "not due". The backend omits `expiresAt` for
 * consents that do not expire, and a date this code cannot read is not grounds
 * to interrupt a user whose face authentication is working.
 */

/** Days before expiry at which the warning and prompt appear. */
export const RECONSENT_WINDOW_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Single read point for the re-consent state, derived from the landing-page
 * payload. Takes the consent list as given: the caller already knows this
 * endpoint returns only the `impr.biometrics` consent, and the earliest expiry
 * across the list is the one that governs.
 *
 * `now` is a parameter so the window boundary is exercisable without freezing
 * the clock.
 */
export const isBioReconsentDue = (
  consentInfos?: readonly ConsentInfo[],
  now: number = Date.now(),
): boolean => {
  const expiries = (consentInfos ?? [])
    .map(consent => (consent.expiresAt ? Date.parse(consent.expiresAt) : Number.NaN))
    .filter(expiry => !Number.isNaN(expiry));

  if (expiries.length === 0) {
    return false;
  }

  // Days are counted from the exact instant rather than from calendar
  // boundaries, so the window does not shift with the viewer's timezone.
  // Truncating towards zero puts the boundary where a user reading a calendar
  // would: 14 days and 23 hours out is still "14 days", so it is inside the
  // window, and anything past the expiry instant is <= 0.
  const daysUntilExpiry = Math.trunc((Math.min(...expiries) - now) / MS_PER_DAY);

  return daysUntilExpiry <= RECONSENT_WINDOW_DAYS;
};
